import {
  Alert,
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
} from "@mui/material";
import * as React from "react";

import {
  WorkspaceAccessibilityLevel,
  WorkspaceOverallAccessibility,
} from "@/shared/domain/workspaces";

import styles from "../ShareWorkspaceModal.module.css";

interface AccessibilityDropdownProps {
  currentStatus: WorkspaceOverallAccessibility | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onAccessibilityChange: (
    newAccessibility: WorkspaceAccessibilityLevel,
  ) => void;
}

/**
 * Maps the overall accessibility status to a user-friendly display label.
 */
function getStatusLabel(status: WorkspaceOverallAccessibility): string {
  switch (status) {
    case "restricted":
      return "Restricted";
    case "partially_public":
      return "Partially Public";
    case "public":
      return "Public";
  }
}

/**
 * Maps the overall accessibility status to a description.
 */
function getStatusDescription(status: WorkspaceOverallAccessibility): string {
  switch (status) {
    case "restricted":
      return "Only people in your organization can access this workspace and its data.";
    case "partially_public":
      return "This workspace and some of its datasets have been made accessible to users outside your organization, but others are restricted.";
    case "public":
      return "Any Roboto user with this link, including those outside your organization, can access this workspace and its data.";
  }
}

/**
 * Dropdown component for viewing and changing workspace accessibility.
 *
 * Shows the current accessibility status and allows users to change between
 * Restricted and Public states. The "Partially Public" state is only
 * displayed when it's the current state and cannot be selected as a target.
 */
export const AccessibilityDropdown: React.FC<AccessibilityDropdownProps> = ({
  currentStatus,
  isLoading,
  isError,
  onRetry,
  onAccessibilityChange,
}) => {
  const handleChange = React.useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      if (value === "restricted" || value === "public") {
        onAccessibilityChange(value);
      }
    },
    [onAccessibilityChange],
  );

  if (isLoading) {
    return (
      <div className={styles.accessibilitySection}>
        <Skeleton variant="text" width={100} height={20} />
        <Skeleton variant="rectangular" height={40} />
      </div>
    );
  }

  if (isError || currentStatus === undefined) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        Failed to load accessibility settings.
      </Alert>
    );
  }

  return (
    <div className={styles.accessibilitySection}>
      <p className={styles.accessibilityLabel}>Accessibility</p>
      <FormControl fullWidth size="small">
        <Select
          id="accessibility-select"
          value={currentStatus}
          onChange={handleChange}
          MenuProps={{
            PaperProps: {
              sx: { width: "inherit" },
            },
          }}
        >
          <MenuItem value="restricted">
            <div className={styles.menuItemContent}>
              <span className={styles.menuItemLabel}>
                {getStatusLabel("restricted")}
              </span>
              <span className={styles.menuItemDescription}>
                {getStatusDescription("restricted")}
              </span>
            </div>
          </MenuItem>

          {/* Only show Partially Public when it's the current state */}
          {currentStatus === "partially_public" && (
            <MenuItem value="partially_public" disabled>
              <div className={styles.menuItemContent}>
                <span className={styles.menuItemLabel}>
                  {getStatusLabel("partially_public")}
                </span>
                <span className={styles.menuItemDescription}>
                  {getStatusDescription("partially_public")}
                </span>
              </div>
            </MenuItem>
          )}

          <MenuItem value="public">
            <div className={styles.menuItemContent}>
              <span className={styles.menuItemLabel}>
                {getStatusLabel("public")}
              </span>
              <span className={styles.menuItemDescription}>
                {getStatusDescription("public")}
              </span>
            </div>
          </MenuItem>
        </Select>
      </FormControl>
    </div>
  );
};
